import { addSlide, newId, touchContentUpdatedAt } from "../model/surfDeck.js";
import { updateSlideInDeck } from "../editor/deckEditor.js";
import { getSurfLayout, normalizeLayoutId } from "../layout/templates.js";
import { applyLayoutToSlide, layersFromLayout } from "../layout/applyLayout.js";
import { outlineLayoutForSection, outlineVisualToSlideRef, sectionToLayoutContents, visualBriefFromSection, } from "./outlineSlide.js";
import { newOutlineSection, normalizeOutlineRole, } from "./types.js";
import { assignCompositionToSection } from "../style/compositionAssign.js";
import { applyCompositionToSlide } from "../layout/compositionLayout.js";
function bulletLine(text) {
    const t = text.trim();
    if (!t)
        return "";
    return t.startsWith("•") ? t : `• ${t}`;
}
function bulletBlock(bullets) {
    return bullets.map(bulletLine).filter(Boolean).join("\n");
}
function extractBulletsFromSlide(slide) {
    const fromLayers = slide.layers
        .filter((l) => l.type === "text" && l.textRole === "bullet")
        .map((l) => l.content.replace(/^•\s*/, "").trim())
        .filter(Boolean);
    if (fromLayers.length)
        return fromLayers;
    const slot = slide.layers.find((l) => l.type === "text" && String(l.style?.slotRole) === "bullets");
    if (!slot?.content.trim())
        return [];
    return slot.content
        .split("\n")
        .map((line) => line.replace(/^•\s*/, "").trim())
        .filter(Boolean);
}
export function sectionFromSlide(slide) {
    const title = slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim() ?? "";
    const subtitle = slide.layers
        .find((l) => l.type === "text" && l.textRole === "subtitle")
        ?.content?.trim();
    const body = slide.layers
        .find((l) => l.type === "text" && l.textRole === "body")
        ?.content?.trim();
    return {
        id: slide.outlineSectionId ?? newId(),
        role: normalizeOutlineRole(slide.outlineRole),
        title,
        subtitle: subtitle || undefined,
        bullets: extractBulletsFromSlide(slide),
        body: body || undefined,
        notes: slide.speakerNotes?.trim() || undefined,
        visual: slide.outlineVisual
            ? {
                kind: slide.outlineVisual.kind,
                caption: slide.outlineVisual.caption,
                imageSubject: slide.outlineVisual.imageSubject,
                graphic: slide.outlineVisual.graphic,
                chart: slide.outlineVisual.chart,
                table: slide.outlineVisual.table,
            }
            : undefined,
        layoutHint: slide.layoutHint,
        intent: slide.outlineRole ?? "content",
    };
}
export function deriveOutlineFromDeck(deck) {
    return deck.slides.map((slide) => sectionFromSlide(slide));
}
/** Ensure deck.outline exists and slides are linked 1:1 (legacy decks). */
export function ensureDeckOutline(deck) {
    if (deck.outline?.length) {
        const outline = deck.outline;
        const slides = deck.slides.map((slide, i) => {
            const section = outline[i];
            if (!section)
                return slide;
            return {
                ...slide,
                outlineSectionId: slide.outlineSectionId ?? section.id,
            };
        });
        return { ...deck, slides };
    }
    const outline = [];
    const slides = deck.slides.map((slide) => {
        const section = sectionFromSlide(slide);
        outline.push(section);
        return { ...slide, outlineSectionId: section.id };
    });
    return touchContentUpdatedAt({ ...deck, outline, slides });
}
function findTextLayer(layers, textRole, slotRole) {
    return layers.find((l) => l.type === "text" &&
        (l.textRole === textRole ||
            (slotRole && String(l.style?.slotRole) === slotRole)));
}
function layerFromLayoutSlot(slide, slotRole, content, textRole) {
    const layout = getSurfLayout(normalizeLayoutId(slide.layout));
    if (!layout)
        return null;
    const slot = layout.slots.find((s) => s.role === slotRole);
    if (!slot)
        return null;
    const z = slide.layers.length ? Math.max(...slide.layers.map((l) => l.z)) + 1 : 1;
    return {
        id: newId(),
        type: "text",
        x: slot.rect.x,
        y: slot.rect.y,
        w: slot.rect.w,
        h: slot.rect.h,
        z,
        rotation: 0,
        content,
        textRole: slot.textRole ?? textRole,
        style: { slotRole: slot.role },
    };
}
function applySectionTextToSlideLayers(slide, section) {
    let layers = [...slide.layers];
    const contents = sectionToLayoutContents(section);
    const upsert = (textRole, content, slotRole) => {
        if (!content.trim() && textRole !== "title")
            return;
        const existing = findTextLayer(layers, textRole, slotRole);
        if (existing) {
            layers = layers.map((l) => (l.id === existing.id ? { ...l, content } : l));
            return;
        }
        const created = layerFromLayoutSlot(slide, slotRole ?? textRole, content, textRole);
        if (created)
            layers.push(created);
    };
    upsert("title", section.title);
    const subtitle = section.subtitle?.trim() ||
        (slide.layout === "cover" ? contents.subtitle : "") ||
        "";
    if (subtitle)
        upsert("subtitle", subtitle, "subtitle");
    const bulletsSlot = findTextLayer(layers, "bullet", "bullets");
    const bulletLayers = layers.filter((l) => l.type === "text" && l.textRole === "bullet");
    const block = bulletBlock(section.bullets);
    if (bulletsSlot) {
        layers = layers.map((l) => l.id === bulletsSlot.id ? { ...l, content: block || l.content } : l);
    }
    else if (bulletLayers.length > 0) {
        layers = layers.map((l) => {
            if (l.type !== "text" || l.textRole !== "bullet")
                return l;
            const bi = bulletLayers.findIndex((bl) => bl.id === l.id);
            const text = section.bullets[bi]?.trim() ?? "";
            return { ...l, content: text ? bulletLine(text) : "" };
        });
        const template = bulletLayers[0];
        for (let i = bulletLayers.length; i < section.bullets.length; i += 1) {
            if (!template)
                break;
            const text = section.bullets[i].trim();
            if (!text)
                continue;
            layers.push({
                ...structuredClone(template),
                id: newId(),
                content: bulletLine(text),
                y: Math.min(0.92, template.y + (i - bulletLayers.length + 1) * 0.045),
                z: template.z + i - bulletLayers.length + 1,
            });
        }
    }
    else if (block) {
        upsert("bullet", block, "bullets");
    }
    if (section.body?.trim()) {
        upsert("body", section.body, "body");
    }
    return {
        ...slide,
        layers,
        speakerNotes: section.notes ?? slide.speakerNotes,
    };
}
export function applyOutlineSectionToSlide(deck, sectionId) {
    const outline = deck.outline ?? [];
    const section = outline.find((s) => s.id === sectionId);
    if (!section)
        return deck;
    const slide = deck.slides.find((s) => s.outlineSectionId === sectionId);
    if (!slide)
        return deck;
    return updateSlideInDeck(deck, slide.id, (s) => applySectionTextToSlideLayers(s, section));
}
export function regenerateSlideTextFromOutline(deck, sectionId) {
    const ids = sectionId
        ? [sectionId]
        : (deck.outline ?? []).map((s) => s.id);
    let next = deck;
    for (const id of ids) {
        next = applyOutlineSectionToSlide(next, id);
    }
    return next;
}
export function setDeckOutline(deck, outline, opts) {
    let next = touchContentUpdatedAt({ ...deck, outline });
    if (opts?.applyToSlides) {
        for (const section of outline) {
            next = applyOutlineSectionToSlide(next, section.id);
        }
    }
    return next;
}
export function updateOutlineSectionInDeck(deck, sectionId, patch) {
    const outline = (deck.outline ?? []).map((s) => s.id === sectionId ? { ...s, ...patch, id: s.id } : s);
    let next = touchContentUpdatedAt({ ...deck, outline });
    next = applyOutlineSectionToSlide(next, sectionId);
    return next;
}
function slideFromOutlineSection(deck, section, slideIndex) {
    const layoutId = outlineLayoutForSection(section);
    let next = addSlide(deck, layoutId);
    const idx = next.slides.length - 1;
    const slideId = next.slides[idx].id;
    const contents = sectionToLayoutContents(section);
    let slide = applyLayoutToSlide(next.slides[idx], layoutId, contents, {
        theme: next.theme,
        autoArrange: true,
    });
    const compositionKind = assignCompositionToSection(section, slideIndex);
    slide = applyCompositionToSlide({
        ...slide,
        speakerNotes: section.notes,
        outlineRole: section.role,
        layoutHint: section.layoutHint ?? layoutId,
        outlineVisual: outlineVisualToSlideRef(section.visual),
        visualBrief: visualBriefFromSection(section),
        outlineSectionId: section.id,
    }, compositionKind, next.theme);
    next = touchContentUpdatedAt({
        ...next,
        slides: next.slides.map((s, i) => (i === idx ? slide : s)),
    });
    return { deck: next, slideId };
}
export function addOutlineSectionToDeck(deck, partial, index) {
    const ensured = ensureDeckOutline(deck);
    const section = newOutlineSection(partial);
    const outline = [...(ensured.outline ?? [])];
    const at = index == null ? outline.length : Math.max(0, Math.min(index, outline.length));
    outline.splice(at, 0, section);
    const { deck: withSlide } = slideFromOutlineSection(ensured, section, at);
    const slides = [...withSlide.slides];
    const newSlide = slides.pop();
    slides.splice(at, 0, newSlide);
    return touchContentUpdatedAt({ ...deck, outline, slides });
}
export function insertBlankSlide(deck, atIndex, layout = "title-body") {
    const section = newOutlineSection({
        title: "Nuova slide",
        layoutHint: layout,
        intent: "content",
    });
    return insertSlideFromSection(deck, section, atIndex);
}
export function insertSlideFromSection(deck, section, atIndex) {
    const ensured = ensureDeckOutline(deck);
    const at = Math.max(0, Math.min(atIndex, (ensured.outline ?? []).length));
    const outline = [...(ensured.outline ?? [])];
    outline.splice(at, 0, section);
    const { deck: withSlide, slideId } = slideFromOutlineSection(ensured, section, at);
    const slides = [...withSlide.slides];
    const newSlide = slides.pop();
    slides.splice(at, 0, newSlide);
    return {
        deck: touchContentUpdatedAt({ ...withSlide, outline, slides }),
        slideId,
    };
}
export function moveSlideInDeck(deck, from, to) {
    if (from === to)
        return deck;
    const count = deck.slides.length;
    if (from < 0 || from >= count || to < 0 || to >= count)
        return deck;
    if (deck.outline?.length) {
        return reorderOutlineSectionsInDeck(deck, from, to);
    }
    const slides = [...deck.slides];
    const [item] = slides.splice(from, 1);
    if (!item)
        return deck;
    slides.splice(to, 0, item);
    return touchContentUpdatedAt({ ...deck, slides });
}
export function removeOutlineSectionFromDeck(deck, sectionId) {
    const outline = (deck.outline ?? []).filter((s) => s.id !== sectionId);
    const slides = deck.slides.filter((s) => s.outlineSectionId !== sectionId);
    return touchContentUpdatedAt({ ...deck, outline, slides });
}
export function reorderOutlineSectionsInDeck(deck, fromIndex, toIndex) {
    const outline = [...(deck.outline ?? [])];
    if (fromIndex < 0 || fromIndex >= outline.length)
        return deck;
    if (toIndex < 0 || toIndex >= outline.length)
        return deck;
    const [item] = outline.splice(fromIndex, 1);
    if (!item)
        return deck;
    outline.splice(toIndex, 0, item);
    const slideBySection = new Map(deck.slides
        .filter((s) => s.outlineSectionId)
        .map((s) => [s.outlineSectionId, s]));
    const slides = [];
    for (const section of outline) {
        const slide = slideBySection.get(section.id);
        if (slide)
            slides.push(slide);
    }
    for (const slide of deck.slides) {
        if (!slide.outlineSectionId || !outline.some((s) => s.id === slide.outlineSectionId)) {
            slides.push(slide);
        }
    }
    return touchContentUpdatedAt({ ...deck, outline, slides });
}
export function syncOutlineFromSlideText(deck, slideId, layerPatch) {
    const slide = deck.slides.find((s) => s.id === slideId);
    if (!slide?.outlineSectionId || !deck.outline?.length)
        return deck;
    const sectionId = slide.outlineSectionId;
    let section = deck.outline.find((s) => s.id === sectionId);
    if (!section)
        return deck;
    if (layerPatch?.textRole && layerPatch.content !== undefined) {
        const content = layerPatch.content;
        switch (layerPatch.textRole) {
            case "title":
                section = { ...section, title: content.trim() };
                break;
            case "subtitle":
                section = { ...section, subtitle: content.trim() || undefined };
                break;
            case "body":
                section = { ...section, body: content.trim() || undefined };
                break;
            case "bullet": {
                const bullets = [...section.bullets];
                const bulletLayers = slide.layers.filter((l) => l.type === "text" && l.textRole === "bullet");
                const layerId = layerPatch.layerId;
                const bi = layerId ? bulletLayers.findIndex((l) => l.id === layerId) : 0;
                const idx = bi >= 0 ? bi : 0;
                bullets[idx] = content.replace(/^•\s*/, "").trim();
                section = { ...section, bullets };
                break;
            }
            default:
                break;
        }
    }
    else {
        section = sectionFromSlide(slide);
        section = { ...section, id: sectionId };
    }
    const outline = deck.outline.map((s) => (s.id === sectionId ? section : s));
    return touchContentUpdatedAt({ ...deck, outline });
}
/** @internal — test helper for layout contents mapping */
export function layoutContentsForSection(section) {
    return sectionToLayoutContents(section);
}
/** @internal — test helper */
export function previewLayersFromLayout(layoutId, contents) {
    const layout = getSurfLayout(normalizeLayoutId(layoutId));
    if (!layout)
        return [];
    return layersFromLayout(layout, contents);
}
